import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { AppDispatch, RootState } from '../../store'

import { formatItem } from './helpers'
import {
  getCustomFields,
  postCustomFields,
  putCustomFields
} from '../../lib/api'

type CustomFieldState = {
  customFields: LocalCustomField[]
  loaded: boolean
  loading: boolean
  error: any
}

const initialState: CustomFieldState = {
  customFields: [],
  loaded: false,
  loading: false,
  error: null
}

const customFieldsSlice = createSlice({
  name: 'customFields',
  initialState,
  reducers: {
    loading: (state: CustomFieldState) => {
      state.loading = true
    },

    load: (
      state: CustomFieldState,
      action: PayloadAction<APICustomField[]>
    ) => {
      state.customFields = action.payload.map(item => formatItem(item))
      state.loaded = true
      state.loading = false
    },

    loadPartial: (
      state: CustomFieldState,
      action: PayloadAction<APICustomField[]>
    ) => {
      // Add or replace based on payload ids
      action.payload.forEach(pl => {
        const item = formatItem(pl)
        const idx = state.customFields.findIndex(other => other.id === item.id)

        // Remove archived field from store
        if (item.archived_at) {
          if (idx >= 0) {
            state.customFields.splice(idx, 1)
          }
          return
        }

        // Update existing field
        if (idx >= 0) {
          state.customFields[idx] = item
          return
        }

        // Add new field
        state.customFields.push(item)
      })

      state.loaded = true
      state.loading = false
    },

    failed: (state: CustomFieldState, action: PayloadAction<any>) => {
      state.error = action.payload
      state.loading = false
      state.loaded = true
    },

    clearFailed: (state: CustomFieldState) => {
      state.error = null
    }
  }
})

export const { loading, load, loadPartial, failed, clearFailed } =
  customFieldsSlice.actions

// Asynchronous thunk action
export const fetchCustomFields = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(loading())
      const data = await getCustomFields()
      dispatch(load(data))
    } catch (error) {
      console.error('Fetch error in custom fields slice')
      dispatch(failed(error))
      setTimeout(() => {
        dispatch(clearFailed())
      }, 2500)
    }
  }
}

// Asynchronous thunk action
export const createCustomFields = (
  data: CustomFieldUpdate[],
  cb?: CallableFunction
) => {
  return async (dispatch: AppDispatch) => {
    try {
      if (data.length > 0) {
        const customFields = await postCustomFields(data)
        dispatch(loadPartial(customFields))
        cb()
      }
    } catch (error) {
      console.error('Post error in custom fields slice')
      dispatch(failed(error))
      setTimeout(() => {
        dispatch(clearFailed())
      }, 2500)
    }
  }
}

// Asynchronous thunk action
export const updateCustomFields = (
  data: CustomFieldUpdate[],
  cb: CallableFunction
) => {
  return async (dispatch: AppDispatch) => {
    try {
      if (data.length > 0) {
        const customFields = await putCustomFields(data)
        dispatch(loadPartial(customFields))
        cb()
      }
    } catch (error) {
      dispatch(failed(error))
      console.error('Put error in custom fields slice')
      setTimeout(() => {
        dispatch(clearFailed())
      }, 2500)
    }
  }
}

export const selectCustomFields = (state: RootState) => state.customFields

export default customFieldsSlice.reducer
